"use client";

import { hasBrowserAnalyticsConsent } from "~/lib/consent-region";
import { capturePostHogClientException } from "~/lib/posthog/error-tracking";
import { ortoRnids } from "~/styles/fonts";
import { BRAND_COLORS } from "~/brand/identity";
import { LogoMark } from "~/components/brand/logo-mark";
import { useEffect } from "react";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    if (!hasBrowserAnalyticsConsent()) {
      return;
    }

    capturePostHogClientException(error, { source: "global_error" });
  }, [error]);

  return (
    <html lang="sr" className={ortoRnids.className}>
      <body style={{ margin: 0 }}>
        <style>{`
          * { box-sizing: border-box; }

          .error-page {
            min-height: 100vh;
            min-height: 100dvh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1.5rem;
            background: ${BRAND_COLORS.paper};
            color: ${BRAND_COLORS.ink};
          }

          .error-panel {
            width: 100%;
            max-width: 70rem;
            overflow: hidden;
            border-radius: 0.75rem;
            border: 1px solid ${BRAND_COLORS.hairline};
            background: ${BRAND_COLORS.paper};
          }

          .error-content {
            padding: 3rem 2rem;
          }

          .error-visual {
            display: none;
          }

          .brand {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            margin: 0 0 2.5rem;
            color: ${BRAND_COLORS.forest};
            font-size: 1.125rem;
            font-weight: 700;
            letter-spacing: -0.025em;
            text-decoration: none;
          }

          .brand-mark { width: 1.5rem; height: 1.7rem; }
          .brand-word span { color: ${BRAND_COLORS.teal}; }

          .error-code {
            display: inline-block;
            margin-bottom: 1.25rem;
            font-size: 0.875rem;
            font-weight: 700;
            color: ${BRAND_COLORS.teal};
          }

          .title {
            font-size: 2rem;
            font-weight: 500;
            line-height: 1.1;
            color: ${BRAND_COLORS.ink};
            margin: 0 0 0.75rem;
          }

          .desc {
            font-size: 1rem;
            line-height: 1.6;
            color: ${BRAND_COLORS.mutedForeground};
            margin: 0;
          }

          .actions {
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
            gap: 0.75rem;
            margin-top: 2rem;
          }

          .btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-family: inherit;
            font-weight: 500;
            font-size: 0.875rem;
            border: 1px solid transparent;
            cursor: pointer;
            border-radius: 9999px;
            height: 2.75rem;
            padding: 0 1.5rem;
            transition: background 0.15s, border-color 0.15s;
            text-decoration: none;
          }

          .btn:focus-visible, .support-link:focus-visible, .brand:focus-visible {
            outline: 2px solid ${BRAND_COLORS.teal};
            outline-offset: 3px;
          }

          .btn-primary {
            color: ${BRAND_COLORS.white};
            background: ${BRAND_COLORS.teal};
          }
          .btn-primary:hover { background: ${BRAND_COLORS.tealHover}; }

          .btn-secondary {
            color: ${BRAND_COLORS.ink};
            border-color: ${BRAND_COLORS.controlEdge};
            background: ${BRAND_COLORS.paper};
          }
          .btn-secondary:hover { background: ${BRAND_COLORS.quietPaper}; }

          .support-link {
            color: ${BRAND_COLORS.teal};
            text-decoration: underline;
            text-underline-offset: 3px;
          }

          .support {
            margin-top: 1.5rem;
            font-size: 0.875rem;
            color: ${BRAND_COLORS.mutedForeground};
          }

          @media (min-width: 640px) {
            .title { font-size: 2.5rem; }
            .error-content { padding: 4rem; }
          }

          @media (min-width: 768px) {
            .error-panel { display: grid; grid-template-columns: 1fr 0.8fr; }
            .error-visual {
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 26rem;
              border-left: 1px solid ${BRAND_COLORS.hairline};
              background: ${BRAND_COLORS.quietPaper};
              color: ${BRAND_COLORS.teal};
              font-size: clamp(8rem, 15vw, 16rem);
              line-height: 1;
              letter-spacing: -0.1em;
            }
          }
        `}</style>

        <main className="error-page">
          <section className="error-panel">
            <div className="error-content">
              <a
                href="/"
                className="brand"
                aria-label="Rešimo Probleme, početna stranica"
              >
                <LogoMark className="brand-mark" decorative />
                <span className="brand-word">
                  rešimo<span>probleme</span>
                </span>
              </a>
              <div className="error-code">Došlo je do greške</div>
              <h1 className="title">Stranica trenutno ne radi.</h1>
              <p className="desc">
                Pokušaj ponovo ili se vrati na početnu stranicu.
              </p>
              <div className="actions">
                <button onClick={() => reset()} className="btn btn-primary">
                  Pokušaj ponovo
                </button>
                <a href="/" className="btn btn-secondary">
                  Na početnu
                </a>
              </div>
              <p className="support">
                Ako se greška ponovi, kontaktiraj našu{" "}
                <a
                  href="mailto:podrska@resimoprobleme.com"
                  className="support-link"
                >
                  podršku
                </a>
                .
              </p>
            </div>
            <div className="error-visual" aria-hidden="true">
              500
            </div>
          </section>
        </main>
      </body>
    </html>
  );
}
